import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar, Toast, Dialog, Skeleton, Empty, Tag, Tabs, Search, Badge, DropdownMenu, Cell } from 'react-vant';
import { 
  ArrowLeft, Edit, DeleteO, Plus, CalendarO, FriendsO, 
  ManagerO, LocationO, StarO, BullhornO, PhotoO, Passed, ClockO
} from '@react-vant/icons';
import { 
  getSessions, deleteSession, 
  getClubs, deleteClub, getClubStatistics, getPendingLeaveCounts 
} from '../../api/club';
import request from '../../api/request';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import RegistrationList from './components/RegistrationList';

const ClubWorkspace = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector(state => state.user);
  const isAdmin = userInfo?.role === 'admin';
  
  // 管理员默认Tab：社团批次；教师默认Tab：我的办公
  const [activeTab, setActiveTab] = useState(isAdmin ? 'sessions' : 'my-work');
  const [sessions, setSessions] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [pendingCounts, setPendingCounts] = useState({});
  const [academicYear, setAcademicYear] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);

  // 加载学年选项
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const res = await request.get('/academic-year/options/');
        if (res.success && res.options) {
          const options = [
            { text: '全部学年', value: 'all' },
            ...res.options.map(o => ({ text: String(o.label), value: String(o.value) }))
          ];
          setYearOptions(options);
        }
      } catch (e) {
        console.error('获取学年选项失败:', e);
      }
    };
    fetchYears();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // 获取当前学年
      let currentYear = academicYear;
      if (!currentYear) {
        try {
          const yearRes = await request.get('/academic-year/current/');
          currentYear = yearRes.data?.current_academic_year || yearRes.current_academic_year;
          if (currentYear) setAcademicYear(currentYear);
        } catch (e) {
          console.error('获取学年失败:', e);
        }
      }
      
      const sessionParams = { page: 1, page_size: 100 };
      if (currentYear && currentYear !== 'all') {
        sessionParams.academic_year = currentYear;
      }
      
      const [sessionsRes, clubsRes, statsRes, countsRes] = await Promise.all([
        getSessions(sessionParams).catch(() => ({ items: [] })),
        isAdmin ? getClubs({ page: 1, page_size: 100 }).catch(() => ({ items: [] })) : Promise.resolve({ items: [] }),
        isAdmin ? getClubStatistics(currentYear && currentYear !== 'all' ? { academic_year: currentYear } : undefined).catch(() => null) : Promise.resolve(null),
        getPendingLeaveCounts().catch(() => ({}))
      ]);
      
      setSessions(sessionsRes.items || []);
      setClubs(clubsRes.items || []);
      setStats(statsRes);
      setPendingCounts(countsRes || {});
    } catch (error) {
      Toast.fail('获取数据失败');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, academicYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 计算待审核请假总数
  const totalPendingLeaves = useMemo(() => {
    return Object.values(pendingCounts).reduce((acc, count) => acc + (Number(count) || 0), 0);
  }, [pendingCounts]);

  const handleDeleteSession = (id) => {
    Dialog.confirm({
      title: '删除确认',
      message: '确定要删除这个报名批次吗？这将删除该批次下所有的报名记录！',
      onConfirm: async () => {
        try {
          await deleteSession(id);
          Toast.success('删除成功');
          fetchData();
        } catch (error) {
          Toast.fail('删除失败');
        }
      }
    });
  };

  const handleDeleteClub = (id, name) => {
    Dialog.confirm({
      title: '删除确认',
      message: `确定要删除社团【${name}】吗？这将同时删除该社团下的所有报名批次和记录。`,
      onConfirm: async () => {
        try {
          await deleteClub(id);
          Toast.success('删除成功');
          fetchData();
        } catch (error) {
          Toast.fail('删除失败');
        }
      }
    });
  };

  const getStatusTag = (status) => {
    const s = (status || '').toLowerCase().trim();
    switch (s) {
      case 'open':
      case 'active': return <Tag type="success">报名中</Tag>;
      case 'upcoming': return <Tag type="primary">未开始</Tag>;
      case 'ongoing': return <Tag type="warning">活动中</Tag>;
      case 'ended': return <Tag type="default">已结束</Tag>;
      case 'closed': return <Tag type="danger">已关闭</Tag>;
      default: return <Tag>{status}</Tag>;
    }
  };

  // 渲染统计栏（四联数据看板）
  const renderSummary = () => {
    // 如果是教师端且后端未返回stats，则从本地sessions计算
    let displayStats = stats;
    if (!displayStats && sessions.length > 0) {
      const totalClubs = sessions.length;
      const totalCapacity = sessions.reduce((sum, s) => sum + (s.max_capacity || 0), 0);
      const totalRegs = sessions.reduce((sum, s) => sum + (s.current_count || 0), 0);
      const rate = totalCapacity > 0 ? Math.round((totalRegs / totalCapacity) * 100) : 0;
      displayStats = {
        total_clubs: totalClubs,
        total_capacity: totalCapacity,
        total_registrations: totalRegs,
        registration_rate: rate
      };
    }
    if (!displayStats) return null;

    return (
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        background: '#fff', 
        padding: '18px 0', 
        marginBottom: '10px',
        borderBottom: '1px solid #f1f5f9'
      }}>
        {[
          { label: '社团总数', value: displayStats.total_clubs ?? 0, color: '#0f172a' },
          { label: '总名额', value: displayStats.total_capacity ?? 0, color: '#0f172a' },
          { label: '已报名', value: displayStats.total_registrations ?? 0, color: '#056b4f' },
          { label: '报名率', value: `${displayStats.registration_rate ?? 0}%`, color: '#00b578' }
        ].map((item, idx) => (
          <div key={idx} style={{ textAlign: 'center', borderRight: idx < 3 ? '1px solid #e2e8f0' : 'none' }}>
            <div style={{ fontSize: '18px', fontWeight: 800, color: item.color }}>{item.value}</div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{item.label}</div>
          </div>
        ))}
      </div>
    );
  };

  // 快捷金刚区卡片（双端对齐设计：全局请假 + 全校通知）
  const renderQuickActions = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', padding: '12px', marginBottom: '8px' }}>
      {[
        {
          title: '全局请假',
          subTitle: '审批全校申请',
          icon: <div style={{ fontSize: '24px', lineHeight: 1 }}>📝</div>,
          badge: totalPendingLeaves,
          onClick: () => navigate('/clubs/teacher/leave-review'),
          iconBg: '#ffeef0',
        },
        {
          title: '全校通知',
          subTitle: '发布重要公告',
          icon: <BullhornO size={24} color="#056b4f" />,
          badge: 0,
          onClick: () => navigate('/clubs/teacher/broadcast'),
          iconBg: '#e8f6f0',
        }
      ].map((item, idx) => (
        <div 
          key={idx}
          onClick={item.onClick}
          style={{ 
            background: '#fff', padding: '15px', borderRadius: '16px',
            border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center',
            gap: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
            cursor: 'pointer'
          }}
        >
          <div style={{ 
            width: '46px', height: '46px', borderRadius: '12px', background: item.iconBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            {item.badge > 0 ? (
              <Badge content={item.badge > 99 ? '99+' : item.badge} style={{ '--rv-badge-size': '16px' }}>{item.icon}</Badge>
            ) : item.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '15px' }}>{item.title}</div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{item.subTitle}</div>
          </div>
        </div>
      ))}
    </div>
  );

  // 渲染社团卡片 (通用)
  const renderSessionCard = (session, showAdminOps = false) => (
    <div 
      key={session.id}
      style={{
        background: '#fff',
        borderRadius: '18px',
        padding: '16px',
        marginBottom: '14px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
        border: '1px solid #f1f5f9'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0, marginRight: '8px' }}>
          <span style={{ fontSize: '17px', fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {session.club_name}
          </span>
          {showAdminOps && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              <Edit size={16} color="#94a3b8" onClick={() => navigate(`/clubs/admin/session-edit/${session.id}`)} style={{ cursor: 'pointer' }} />
              <DeleteO size={16} color="#94a3b8" onClick={() => handleDeleteSession(session.id)} style={{ cursor: 'pointer' }} />
            </div>
          )}
        </div>
        {getStatusTag(session.status)}
      </div>
      
      <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '14px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ClockO size={14} /> <span>{session.academic_year} · 第{session.round_number}轮</span>
        </div>
        <span style={{ color: '#e2e8f0' }}>|</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <FriendsO size={14} /> <span>已报 {session.current_count} / {session.max_capacity}人</span>
        </div>
        {session.instructor_name && (
          <>
            <span style={{ color: '#e2e8f0' }}>|</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ManagerO size={14} /> <span>{session.instructor_name}</span>
            </div>
          </>
        )}
      </div>

      {/* 核心操作按钮条（双端 5 键金刚操作条：点名、相册、审批、评价、广播） */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        paddingTop: '12px',
        borderTop: '1px solid #f8fafc'
      }}>
        {[
          { label: '点名', icon: <Passed size={22} color="#056b4f" />, bg: '#e8f6f0', path: `/clubs/teacher/attendance/${session.id}` },
          { label: '相册', icon: <PhotoO size={22} color="#00b578" />, bg: '#ecfdf5', path: `/clubs/teacher/album/${session.id}` },
          { label: '审批', icon: (
            <Badge content={pendingCounts[session.id] || 0} showZero={false} style={{ '--rv-badge-size': '15px' }}>
              <Passed size={22} color="#e11d48" />
            </Badge>
          ), bg: '#fff1f2', path: `/clubs/teacher/leave-review/${session.id}` },
          { label: '评价', icon: <StarO size={22} color="#7c3aed" />, bg: '#f5f3ff', path: `/clubs/teacher/evaluation/${session.id}` },
          { label: '广播', icon: <BullhornO size={22} color="#fa8c16" />, bg: '#fff7e6', path: `/clubs/teacher/broadcast/${session.id}` },
        ].map((btn, idx) => (
          <div key={idx} onClick={() => navigate(btn.path)} style={{ textAlign: 'center', flex: 1, cursor: 'pointer' }}>
            <div style={{ 
              width: '40px', height: '40px', borderRadius: '12px', background: btn.bg, 
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 6px'
            }}>
              {btn.icon}
            </div>
            <div style={{ fontSize: '11px', color: '#475569', fontWeight: 500 }}>{btn.label}</div>
          </div>
        ))}
      </div>
    </div>
  );

  // =============================================
  // Tab 内容渲染函数
  // =============================================

  // 社团批次 Tab（管理员视角：全部批次 + 统计 + 快捷入口）
  const renderSessionsTab = () => (
    <>
      {renderSummary()}
      {renderQuickActions()}

      <div style={{ padding: '12px', paddingTop: 0 }}>
        {loading ? <Skeleton title row={10} /> : (
          sessions.length === 0 ? <Empty description="暂无报名批次" /> : (
            sessions.map(session => renderSessionCard(session, true))
          )
        )}
      </div>
    </>
  );

  // 社团库 Tab（管理员：全局社团 CRUD）
  const renderClubLibTab = () => (
    <div style={{ padding: '12px' }}>
      <Search value={searchText} onChange={setSearchText} placeholder="搜索社团、教师或地点" style={{ background: 'transparent', padding: '0 0 12px 0' }} />
      {loading ? <Skeleton title row={10} /> : (
        clubs.filter(c => c.name.includes(searchText) || (c.instructor_name || '').includes(searchText) || (c.venue || '').includes(searchText)).length === 0 ? (
          <Empty description="暂无社团数据" />
        ) : (
          clubs.filter(c => c.name.includes(searchText) || (c.instructor_name || '').includes(searchText) || (c.venue || '').includes(searchText)).map(club => (
            <div key={club.id} style={{ background: '#fff', borderRadius: '18px', padding: '16px', marginBottom: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.02)', border: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>{club.name}</span>
                    <Tag type={club.status === 'active' ? 'success' : 'default'} plain size="mini">{club.status === 'active' ? '活跃' : '停用'}</Tag>
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8' }}>{club.category || '综合'}</div>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Edit size={18} color="#94a3b8" onClick={() => navigate(`/clubs/admin/club-edit/${club.id}`)} style={{ cursor: 'pointer' }} />
                  <DeleteO size={18} color="#94a3b8" onClick={() => handleDeleteClub(club.id, club.name)} style={{ cursor: 'pointer' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#64748b' }}>
                  <ManagerO size={14} color="#94a3b8" />
                  <span>{club.instructor_name || '未指定'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#64748b' }}>
                  <LocationO size={14} color="#94a3b8" />
                  <span>{club.venue || '未定地点'}</span>
                </div>
              </div>
              {club.description ? (
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px', lineHeight: '18px' }}>
                  {club.description}
                </div>
              ) : null}
            </div>
          ))
        )
      )}
    </div>
  );

  // 我的办公 Tab（教师视角：显示自己负责的批次 + 快捷卡片 + 统计）
  const renderMyWorkTab = () => (
    <>
      {renderSummary()}
      {renderQuickActions()}

      <div style={{ padding: '12px', paddingTop: 0 }}>
        {loading ? <Skeleton title row={5} /> : (
          sessions.length === 0 ? <Empty description="暂无您负责管理的社团" /> : (
            sessions.map(session => renderSessionCard(session, false))
          )
        )}
      </div>
    </>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', paddingBottom: '60px' }}>
      <NavBar 
        title={<span style={{ fontWeight: 800 }}>{isAdmin ? '社团管理中台' : '社团工作空间'}</span>} 
        leftText={<ArrowLeft size={20} />} 
        rightText={<BullhornO size={20} color="#056b4f" />}
        onClickLeft={() => navigate(-1)}
        onClickRight={() => navigate('/clubs/teacher/broadcast')}
        fixed placeholder
      />

      {/* 学年筛选器 */}
      {yearOptions.length > 0 && (
        <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
          <DropdownMenu key={academicYear} activeColor="#056b4f">
            <DropdownMenu.Item 
              title={yearOptions.find(o => o.value === academicYear)?.text || '选择学年'} 
              value={academicYear || ''}
            >
              <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                {yearOptions.map(opt => (
                  <Cell
                    key={opt.value}
                    title={opt.text}
                    onClick={() => setAcademicYear(opt.value)}
                    style={{ color: academicYear === opt.value ? '#056b4f' : 'inherit' }}
                  />
                ))}
              </div>
            </DropdownMenu.Item>
          </DropdownMenu>
        </div>
      )}

      {isAdmin ? (
        /* ====== 管理员视图: 社团批次 | 社团库 | 报名名单 ====== */
        <Tabs active={activeTab} onChange={v => setActiveTab(v)} sticky offsetTop="46px">
          <Tabs.TabPane name="sessions" title="社团批次">
            {renderSessionsTab()}
          </Tabs.TabPane>
          
          <Tabs.TabPane name="club-lib" title="社团库">
            {renderClubLibTab()}
          </Tabs.TabPane>

          <Tabs.TabPane name="registrations" title="报名名单">
            <RegistrationList isAdmin={isAdmin} academicYear={academicYear} />
          </Tabs.TabPane>
        </Tabs>
      ) : (
        /* ====== 教师视图: 我的办公 | 报名名单 ====== */
        <Tabs active={activeTab} onChange={v => setActiveTab(v)} sticky offsetTop="46px">
          <Tabs.TabPane name="my-work" title="我的办公">
            {renderMyWorkTab()}
          </Tabs.TabPane>
          
          <Tabs.TabPane name="registrations" title="报名名单">
            <RegistrationList isAdmin={false} academicYear={academicYear} />
          </Tabs.TabPane>
        </Tabs>
      )}

      {/* 悬浮按钮 - 仅管理员在批次/社团库页显示 */}
      {isAdmin && (activeTab === 'sessions' || activeTab === 'club-lib') && (
        <div 
          onClick={() => activeTab === 'sessions' ? navigate('/clubs/admin/session-edit/new') : navigate('/clubs/admin/club-edit/new')}
          style={{ 
            position: 'fixed', right: '24px', bottom: '32px', width: '56px', height: '56px', borderRadius: '28px', 
            background: 'linear-gradient(135deg, #056b4f 0%, #00b578 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(5, 107, 79, 0.35)', zIndex: 99, cursor: 'pointer'
          }}
        >
          <Plus color="#fff" size={28} />
        </div>
      )}
    </div>
  );
};

export default ClubWorkspace;
